import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Loader2, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useGuru } from '../../../context/GuruContext';
import { useUserAccess } from '../../../context/UserAccessContext';
import { PageTransition } from '../../transitions/PageTransition';

export const PortalGuruPengaturanEkskul: React.FC = () => {
    const navigate = useNavigate();
    const { guruRecords } = useGuru();
    const { userPermissions } = useUserAccess();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const [accessData, setAccessData] = useState<Record<string, string[]>>({});
    const [wakaTeachers, setWakaTeachers] = useState<any[]>([]);

    useEffect(() => {
        fetchData();
    }, [guruRecords, userPermissions]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Find all teachers with 'pg-waka-kesiswaan' permission
            const wakaEmails = userPermissions
                .filter(p => p.allowedMenus.includes('pg-waka-kesiswaan'))
                .map(p => p.email);

            const teachers = guruRecords.filter(g => g.email && wakaEmails.includes(g.email));
            setWakaTeachers(teachers);

            // Fetch current settings from Firestore (we'll store it as a single doc for simplicity, or a collection)
            // Storing as a collection `waka_kesiswaan_access` where doc ID is the teacher's ID
            const q = query(collection(db, 'waka_kesiswaan_access'));
            const snap = await getDocs(q);

            const accessMap: Record<string, string[]> = {};
            snap.docs.forEach(d => {
                accessMap[d.id] = d.data().allowedJenjangs || [];
            });

            setAccessData(accessMap);
        } catch (error) {
            console.error("Failed to fetch ekskul access data", error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleAccess = (teacherId: string, jenjang: string) => {
        setAccessData(prev => {
            const currentAccess = prev[teacherId] || [];
            if (currentAccess.includes(jenjang)) {
                return { ...prev, [teacherId]: currentAccess.filter(j => j !== jenjang) };
            } else {
                return { ...prev, [teacherId]: [...currentAccess, jenjang] };
            }
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Iterate over accessData and save to Firestore
            const promises = Object.entries(accessData).map(async ([teacherId, jenjangs]) => {
                const docRef = doc(db, 'waka_kesiswaan_access', teacherId);
                await setDoc(docRef, { allowedJenjangs: jenjangs }, { merge: true });
            });
            await Promise.all(promises);
            alert("Pengaturan hak akses ekskul berhasil disimpan.");
        } catch (error) {
            console.error("Failed to save ekskul access data", error);
            alert("Gagal menyimpan pengaturan.");
        } finally {
            setSaving(false);
        }
    };

    const ALL_JENJANG = ['PAUDIT', 'SDIT-TQ', 'SMPIT-TQ', 'SMAIT-TQ'];

    const filteredTeachers = wakaTeachers.filter(t =>
        t.nama.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.divisi || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-20 font-sans">
            {/* Header */}
            <div className="bg-white border-b sticky top-0 z-30 shadow-sm">
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/portal-guru/admin/pengaturan')}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div>
                            <h1 className="text-lg font-bold text-gray-900 leading-tight">Akses Ekskul Lintas Jenjang</h1>
                            <p className="text-xs text-gray-500 font-medium">Pengaturan Super Admin</p>
                        </div>
                    </div>
                </div>
            </div>

            <PageTransition className="p-4 max-w-4xl mx-auto space-y-6 mt-4">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <div className="mb-6">
                        <h2 className="text-base font-bold text-gray-900 mb-2">Kelola Hak Akses Waka Kesiswaan</h2>
                        <p className="text-sm text-gray-500">
                            Pilih jenjang yang dapat dikelola oleh masing-masing Waka Kesiswaan untuk fitur Ekstrakurikuler. Jika tidak ada yang dipilih, sistem akan menggunakan divisi bawaan guru tersebut.
                        </p>
                    </div>

                    <div className="relative mb-4">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Cari nama guru atau divisi..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-brand-primary focus:border-brand-primary transition-colors outline-none"
                        />
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center py-10">
                            <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                        </div>
                    ) : filteredTeachers.length === 0 ? (
                        <div className="text-center py-10 text-gray-500 text-sm">
                            Tidak ada guru dengan akses Waka Kesiswaan yang ditemukan.
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredTeachers.map(teacher => {
                                const currentAccess = accessData[teacher.id] || [];

                                return (
                                    <div key={teacher.id} className="border border-gray-100 rounded-xl p-4 hover:border-gray-300 transition-colors">
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-3">
                                            <div>
                                                <h3 className="font-bold text-gray-900">{teacher.nama}</h3>
                                                <p className="text-xs text-gray-500 mt-0.5">Divisi: <span className="font-medium text-gray-700">{teacher.divisi || '-'}</span></p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                            {ALL_JENJANG.map(jenjang => {
                                                const isChecked = currentAccess.includes(jenjang);
                                                return (
                                                    <label key={jenjang} className={`flex items-center justify-center p-2 rounded-lg border cursor-pointer transition-all ${isChecked ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
                                                        <input
                                                            type="checkbox"
                                                            className="sr-only"
                                                            checked={isChecked}
                                                            onChange={() => handleToggleAccess(teacher.id, jenjang)}
                                                        />
                                                        <span className="text-xs font-semibold">{jenjang}</span>
                                                    </label>
                                                )
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="pt-4 pb-8 flex justify-end">
                    <button
                        onClick={handleSave}
                        disabled={saving || loading}
                        className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                        {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
                    </button>
                </div>
            </PageTransition>
        </div>
    );
};
